// Module 1 — Onglet Aperçu : ce qui demande une action (dossier, alertes) puis les derniers
// événements. Aucune donnée détaillée ici : elle est rangée dans les autres onglets.
import { AlertTriangle, CheckCircle2, CircleAlert, CircleDot, History, ShieldCheck, ArrowRight, Lightbulb } from 'lucide-react';
import { Carte, Badge, Bouton, EtatVide, BandeauInfo, formaterDate } from '../../design/composants';

const LIBELLE_ONGLET = { apercu: 'Aperçu', emploi: 'Emploi', personnel: 'Personnel', historique: 'Historique', documents: 'Documents' };

function Point({ point, onglets, onAllerOnglet, Icone, couleur }) {
  const accessible = onglets.includes(point.onglet) && point.onglet !== 'apercu';
  return (
    <li>
      <Icone size={16} color={couleur} aria-hidden="true" style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{point.libelle}</span>
      {accessible && (
        <Bouton petit variante="discret" onClick={() => onAllerOnglet(point.onglet)}>
          Compléter dans {LIBELLE_ONGLET[point.onglet] || point.onglet}<ArrowRight size={13} aria-hidden="true" />
        </Bouton>
      )}
    </li>
  );
}

export default function ApercuSalarie({ synthese, onAllerOnglet }) {
  const { qualite, confidentialite, evenements_recents: evenements, programmes = [], sortie } = synthese;
  const onglets = confidentialite.onglets;

  return (
    <div>
      {sortie && <BandeauInfo ton="neutre" titre="Salarié sorti">Dernier jour travaillé le {formaterDate(sortie.date)} — {sortie.motif}. Le dossier reste consultable ; il n'est plus modifiable que par annulation de la sortie.</BandeauInfo>}
      {programmes.map((p) => <BandeauInfo key={p.id} ton="alerte" titre="Changement programmé">{p.libelle}.</BandeauInfo>)}
      {qualite && (
        <Carte titre="État du dossier" Icone={ShieldCheck}
          action={qualite.etat === 'complet' ? <Badge ton="succes">Complet</Badge>
            : qualite.etat === 'bloquant' ? <Badge ton="danger">Bloquant</Badge> : qualite.etat === 'clos' ? <Badge ton="neutre">Dossier clos</Badge> : <Badge ton="alerte">À compléter</Badge>}>
          <p style={{ margin: '0 0 8px', color: 'var(--sg-texte-2)', fontSize: 'var(--sg-t-petit)', fontWeight: 500 }}>{qualite.explication}</p>
          {['complet', 'clos'].includes(qualite.etat) ? null : (
            <>
              <p style={{ margin: '0 0 8px', color: 'var(--sg-texte-3)', fontSize: 'var(--sg-t-petit)' }}>
                {qualite.bloquants.length > 0
                  ? 'Une information indispensable manque : la gestion RH du dossier ne peut pas être cohérente tant qu\'elle n\'est pas renseignée.'
                  : 'Le dossier est utilisable ; ces informations restent à compléter.'}
              </p>
              <ul className="rh-liste">
                {qualite.bloquants.map((p) => <Point key={p.code} point={p} onglets={onglets} onAllerOnglet={onAllerOnglet} Icone={CircleAlert} couleur="var(--sg-danger)" />)}
                {qualite.a_completer.map((p) => <Point key={p.code} point={p} onglets={onglets} onAllerOnglet={onAllerOnglet} Icone={CircleDot} couleur="var(--sg-alerte)" />)}
              </ul>
            </>
          )}
        </Carte>
      )}

      {qualite?.conseils?.length > 0 && (
        <p style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--sg-t-petit)', color: 'var(--sg-texte-3)', margin: '-4px 2px 16px' }}>
          <Lightbulb size={15} aria-hidden="true" />
          Suggestion : {qualite.conseils.map((c) => c.libelle.toLowerCase()).join(', ')} (sans effet sur l'état du dossier).
        </p>
      )}

      {qualite?.alertes?.length > 0 && (
        <Carte titre="Points de vigilance" Icone={AlertTriangle}>
          <ul className="rh-liste">
            {qualite.alertes.map((p) => <Point key={p.code} point={p} onglets={onglets} onAllerOnglet={onAllerOnglet} Icone={AlertTriangle} couleur="var(--sg-alerte)" />)}
          </ul>
        </Carte>
      )}

      <Carte titre="Derniers événements" Icone={History}
        action={evenements.length > 0 && <Bouton petit variante="discret" onClick={() => onAllerOnglet('historique')}>Tout l'historique<ArrowRight size={13} aria-hidden="true" /></Bouton>}>
        {evenements.length === 0 ? (
          <EtatVide Icone={History} titre="Aucun événement enregistré" texte="Les affectations, changements de contrat, promotions et changements de statut apparaîtront ici." />
        ) : (
          <ul className="rh-liste">
            {evenements.map((e, i) => (
              <li key={e.id || i}>
                <CheckCircle2 size={15} color="var(--sg-texte-4)" aria-hidden="true" />
                <span style={{ width: 92, color: 'var(--sg-texte-3)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{formaterDate(e.date)}</span>
                <span style={{ flex: 1 }}><strong style={{ fontWeight: 600 }}>{e.titre}</strong> — {e.phrase}{e.etat === 'programme' ? ' (programmé)' : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </Carte>

      {!confidentialite.niveaux.includes('personnel') && (
        <p style={{ fontSize: 'var(--sg-t-mini)', color: 'var(--sg-texte-3)', margin: '4px 2px' }}>
          Les informations personnelles, familiales et salariales de ce salarié sont réservées au service RH et au salarié lui-même.
        </p>
      )}
    </div>
  );
}
